// Money as the user wants to see it: their currency and number format
// (Settings → General). The settings live at module level so plain functions
// such as formatCurrency can use them; useUserSettings keeps them current.

const DEFAULT_SETTINGS = { currency: 'INR', numberFormat: 'indian' };

let settings = { ...DEFAULT_SETTINGS };
const listeners = new Set();

export const getMoneySettings = () => settings;

/** Called when the user's settings load or change */
export const setMoneySettings = (next) => {
  const merged = {
    currency: (next && next.currency) || DEFAULT_SETTINGS.currency,
    numberFormat: (next && next.numberFormat) || DEFAULT_SETTINGS.numberFormat,
  };
  if (merged.currency === settings.currency && merged.numberFormat === settings.numberFormat) return;
  settings = merged;
  listeners.forEach(fn => fn(settings));
};

export const onMoneySettingsChange = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

// Indian grouping (1,25,000) or international (125,000)
export const localeFor = (numberFormat = settings.numberFormat) =>
  numberFormat === 'international' ? 'en-US' : 'en-IN';

const currencyFormatter = (decimals) => {
  try {
    return new Intl.NumberFormat(localeFor(), {
      style: 'currency',
      currency: settings.currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  } catch (e) {
    return null;
  }
};

/**
 * @param {number|string} amount
 * @param {{ decimals?: number }} [options] default 2
 */
export const formatMoney = (amount, { decimals = 2 } = {}) => {
  const n = Number(amount) || 0;
  const formatter = currencyFormatter(decimals);
  return formatter ? formatter.format(n) : `${settings.currency} ${n.toFixed(decimals)}`;
};

/** The currency sign for input fields: ₹, $, € ... */
export const currencySymbol = () => {
  const formatter = currencyFormatter(0);
  if (!formatter) return settings.currency;
  const part = formatter.formatToParts(0).find(p => p.type === 'currency');
  return part ? part.value : settings.currency;
};

/** A plain number with the user's grouping */
export const formatNumber = (value) => new Intl.NumberFormat(localeFor()).format(Number(value) || 0);

/**
 * Short amounts for chart axes and tight spaces: ₹950, ₹12k, ₹1.2L, ₹2.5Cr in
 * the Indian format; $950, $12K, $1.2M, $2.5B otherwise.
 */
export const formatMoneyCompact = (value) => {
  const n = Number(value) || 0;
  const sign = n < 0 ? '-' : '';
  const a = Math.abs(n);
  const symbol = currencySymbol();
  const trim = (x) => (x >= 10 ? Math.round(x) : Math.round(x * 10) / 10);
  const steps = settings.numberFormat === 'international'
    ? [[1e9, 'B'], [1e6, 'M'], [1e3, 'K']]
    : [[1e7, 'Cr'], [1e5, 'L'], [1e3, 'k']];
  for (const [size, suffix] of steps) {
    if (a >= size) return `${sign}${symbol}${trim(a / size)}${suffix}`;
  }
  return `${sign}${symbol}${Math.round(a)}`;
};

// Currencies offered in Settings → General
export const CURRENCY_OPTIONS = [
  { code: 'INR', label: 'Indian rupee (₹)' },
  { code: 'USD', label: 'US dollar ($)' },
  { code: 'EUR', label: 'Euro (€)' },
  { code: 'GBP', label: 'British pound (£)' },
  { code: 'AED', label: 'UAE dirham' },
  { code: 'SGD', label: 'Singapore dollar' },
  { code: 'AUD', label: 'Australian dollar' },
  { code: 'CAD', label: 'Canadian dollar' },
  { code: 'JPY', label: 'Japanese yen (¥)' },
  { code: 'CHF', label: 'Swiss franc' },
  { code: 'SAR', label: 'Saudi riyal' },
  { code: 'NZD', label: 'New Zealand dollar' },
];
